import { FlashOn, List } from "@mui/icons-material";
import {
  Alert,
  Box,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import CreatedReports from "./components/CreatedReports";
import QuickActions from "./components/QuickActions";
import { AutoGenerateParams, ReportRequestedParams } from "./types";

const TAB_PARAM = "tab";
const TABS = ["quick-actions", "created"] as const;
type TabName = typeof TABS[number];

const getTabIndexFromParam = (param: string | null): number => {
  const index = TABS.indexOf(param as TabName);
  return index >= 0 ? index : 0;
};

const CREATED_TAB_INDEX = 1;

const tabPanelSx = { pt: 3 };
const snackbarAnchorOrigin = { vertical: "bottom", horizontal: "center" } as const;
const snackbarAlertSx = { width: "100%" };

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`manager-hub-tabpanel-${index}`}
    aria-labelledby={`manager-hub-tab-${index}`}
    sx={tabPanelSx}
  >
    {value === index && children}
  </Box>
);

const a11yProps = (index: number) => ({
  id: `manager-hub-tab-${index}`,
  "aria-controls": `manager-hub-tabpanel-${index}`
});

const AhaMomentsHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(() => getTabIndexFromParam(searchParams.get(TAB_PARAM)));
  const [autoGenerateParams, setAutoGenerateParams] = useState<AutoGenerateParams | null>(null);
  const [submissionSnackbarOpen, setSubmissionSnackbarOpen] = useState(false);

  useEffect(() => {
    const startGenerate = searchParams.get("startGenerate");
    const userParam = searchParams.get("user_email");
    const protocol = searchParams.get("sc_name");
    const timeRange = searchParams.get("time_range") || "last7Days";

    if (startGenerate === "true" && userParam && userParam !== "all" && protocol && protocol !== "all") {
      setAutoGenerateParams({ user: userParam, protocol, timeRange });
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("startGenerate");
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(TAB_PARAM, TABS[newValue]);
    setSearchParams(newParams, { replace: true });
  };

  const handleReportRequested = useCallback((params: ReportRequestedParams) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("user_email", params.user);
    newSearchParams.set("sc_name", params.protocol);
    newSearchParams.set("time_range", params.timeRange);
    newSearchParams.set(TAB_PARAM, TABS[CREATED_TAB_INDEX]);
    setSearchParams(newSearchParams);
    setTabValue(CREATED_TAB_INDEX);
    setAutoGenerateParams(null);
    setSubmissionSnackbarOpen(true);
  }, [searchParams, setSearchParams]);

  const handleAutoGenerateComplete = useCallback(() => {
    setAutoGenerateParams(null);
  }, []);

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Manager Hub
      </Typography>

      <Paper sx={{ px: 2, pt: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Manager Hub tabs"
        >
          <Tab
            icon={<FlashOn />}
            iconPosition="start"
            label="Quick Actions"
            data-testid="manager-hub-tab-quick-actions"
            {...a11yProps(0)}
          />
          <Tab
            icon={<List />}
            iconPosition="start"
            label="Created Reports"
            data-testid="manager-hub-tab-created"
            {...a11yProps(1)}
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <QuickActions
            onReportRequested={handleReportRequested}
            autoGenerateParams={autoGenerateParams}
            onAutoGenerateComplete={handleAutoGenerateComplete}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <CreatedReports />
        </TabPanel>
      </Paper>

      <Snackbar
        open={submissionSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSubmissionSnackbarOpen(false)}
        anchorOrigin={snackbarAnchorOrigin}
      >
        <Alert
          onClose={() => setSubmissionSnackbarOpen(false)}
          severity="info"
          variant="filled"
          sx={snackbarAlertSx}
          data-testid="manager-hub-snackbar-submission"
        >
          Your report is being generated and will be available shortly.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AhaMomentsHub;
